import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNetworkWired,
  faGlobe,
  faCheckCircle,
  faClock,
  faPlus,
  faSync,
  faSpinner,
  faBroadcastTower,
  faStop,
  faTrash,
  faExclamationTriangle,
  faInfoCircle,
  faCopy,
  faLink,
  faUnlink,
  faShieldAlt,
  faChartBar,
  faMapMarkerAlt,
  faWifi,
  faCheck,
  faTag,
  faArrowLeft,
  faSearch,
  faShoppingCart,
  faChevronDown,
  faChevronUp,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CidrRange {
  id?: string;
  cidr: string;
  status: 'advertised' | 'provisioned' | 'pending-provision' | 'pending-de-provision' | 'de-provisioned';
  region: string;
  poolId: string;
  totalIPs: number;
  usedIPs: number;
  description?: string;
  createdAt: string;
  leaseExpiresAt?: string;
}

interface ElasticIP {
  allocationId: string;
  publicIp: string;
  poolId: string;
  cidr: string;
  associatedWith: string | null;
  associationType: 'ec2' | 'nlb' | null;
  region: string;
  allocatedAt: string;
}

interface BYOIPStats {
  totalCidrs: number;
  advertisedCidrs: number;
  provisionedCidrs: number;
  totalIPs: number;
  usedIPs: number;
  availableIPs: number;
}

interface CartItem {
  subnet: LeasingSubnet;
  addedAt: string;
}

interface LeasingSubnet {
  id: string;
  subnet: string;
  subnetSize: string;
  totalIPs: number;
  rir: string;
  geoCountry: string;
  geoCountryName: string;
  geoCity: string;
  subnetPriceMonthly: number;
  pricePerIPMonthly: number;
  countryMatch: 'Full Match' | 'Partial Match' | 'No Match';
  rpki: string;
  rdns: string;
  whoisInetnum: string;
  whoisRoutes: string;
  ipregistryCountry: string;
  ipregistryCountryName: string;
  ipregistryCity: string;
  ipregistryUpdated: string;
  maxmindCountry: string;
  maxmindCountryName: string;
  maxmindCity: string;
  maxmindUpdated: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SUBNET_SIZES = [
  { size: '/24', ips: '256' },
  { size: '/23', ips: '512' },
  { size: '/22', ips: '1,024' },
  { size: '/21', ips: '2,048' },
  { size: '/20', ips: '4,096' },
  { size: '/19', ips: '8,192' },
  { size: '/18', ips: '16,384' },
  { size: '/17', ips: '32,768' },
  { size: '/16', ips: '65,536' },
];

const RIR_OPTIONS = ['AFRINIC', 'APNIC', 'ARIN', 'LACNIC', 'RIPENCC'];

// ─── Mock Data (dashboard) ────────────────────────────────────────────────────

const MOCK_EIPS: ElasticIP[] = [
  {
    allocationId: 'eipalloc-0a1b2c3d4e',
    publicIp: '203.0.113.10',
    poolId: 'ipv4pool-ec2-001',
    cidr: '203.0.113.0/24',
    associatedWith: 'i-0abc123def456',
    associationType: 'ec2',
    region: 'us-east-1',
    allocatedAt: '2025-03-15T12:00:00Z',
  },
  {
    allocationId: 'eipalloc-0b2c3d4e5f',
    publicIp: '203.0.113.11',
    poolId: 'ipv4pool-ec2-001',
    cidr: '203.0.113.0/24',
    associatedWith: null,
    associationType: null,
    region: 'us-east-1',
    allocatedAt: '2025-03-16T09:00:00Z',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getStatusColor = (status: CidrRange['status']) => {
  switch (status) {
    case 'advertised': return 'bg-green-100 text-green-700 border border-green-300';
    case 'provisioned': return 'bg-yellow-100 text-yellow-700 border border-yellow-300';
    case 'pending-provision':
    case 'pending-de-provision': return 'bg-blue-100 text-blue-700 border border-blue-300';
    case 'de-provisioned': return 'bg-gray-100 text-gray-500 border border-gray-300';
    default: return 'bg-gray-100 text-gray-600 border border-gray-300';
  }
};

const getStatusLabel = (status: CidrRange['status']) => {
  switch (status) {
    case 'advertised': return 'ADVERTISED';
    case 'provisioned': return 'PROVISIONED';
    case 'pending-provision': return 'PENDING';
    case 'pending-de-provision': return 'DE-ADVERTISING';
    case 'de-provisioned': return 'DE-PROVISIONED';
    default: return (status as string).toUpperCase();
  }
};

const calcStats = (cidrs: CidrRange[]): BYOIPStats => {
  const totalIPs = cidrs.reduce((s, c) => s + c.totalIPs, 0);
  const usedIPs = cidrs.reduce((s, c) => s + c.usedIPs, 0);
  return {
    totalCidrs: cidrs.length,
    advertisedCidrs: cidrs.filter(c => c.status === 'advertised').length,
    provisionedCidrs: cidrs.filter(c => c.status === 'provisioned').length,
    totalIPs,
    usedIPs,
    availableIPs: totalIPs - usedIPs,
  };
};

// ─── GEO Data Modal ───────────────────────────────────────────────────────────

const GeoDataModal: React.FC<{ subnet: LeasingSubnet; onClose: () => void }> = ({ subnet, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-sm w-full font-mono">
      <div className="p-5">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Geo Data</h2>

        {/* ipregistry */}
        <div className="bg-gray-50 rounded-lg p-4 mb-3 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faGlobe} className="text-white text-xs" />
              </div>
              <span className="font-bold text-sm text-gray-900">ipregistry</span>
            </div>
            <span className="text-xs text-gray-500">Updated: {subnet.ipregistryUpdated}</span>
          </div>
          <div className="h-px bg-gray-200 mb-3" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-500 text-xs">Database</span>
              <a
                href="https://ipregistry.co"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-xs font-medium"
              >
                ipregistry.co ↗
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-xs">Location</span>
              <span className="font-medium text-xs">{subnet.ipregistryCountry}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-xs">Country</span>
              <span className="font-medium text-xs">{subnet.ipregistryCountryName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-xs">City</span>
              <span className="font-medium text-xs">{subnet.ipregistryCity}</span>
            </div>
          </div>
        </div>

        {/* MaxMind */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-blue-900 tracking-tight">⬡ MAXMIND</span>
            </div>
            <span className="text-xs text-gray-500">Updated: {subnet.maxmindUpdated}</span>
          </div>
          <div className="h-px bg-gray-200 mb-3" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-500 text-xs">Database</span>
              <a
                href="https://maxmind.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-xs font-medium"
              >
                maxmind.com ↗
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-xs">Location</span>
              <span className="font-medium text-xs">{subnet.maxmindCountry}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-xs">Country</span>
              <span className="font-medium text-xs">{subnet.maxmindCountryName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-xs">City</span>
              <span className="font-medium text-xs">{subnet.maxmindCity || '—'}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="text-blue-600 font-medium hover:underline text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ─── Add CIDR Modal ───────────────────────────────────────────────────────────

interface AddCidrModalProps {
  onClose: () => void;
  onAdd: (cidr: string, region: string, description: string) => void;
}

const AddCidrModal: React.FC<AddCidrModalProps> = ({ onClose, onAdd }) => {
  const [cidr, setCidr] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(false);

  const regions = [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'ap-south-1', 'ap-southeast-1', 'ap-northeast-1',
    'eu-west-1', 'eu-central-1', 'eu-north-1',
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cidr.trim()) return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 1200));
    onAdd(cidr.trim(), region, description.trim());
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded overflow-hidden max-w-lg w-full shadow-xl">
        <div className="bg-blue-600 px-4 py-3 flex items-center justify-between">
          <div className="text-white">
            <h3 className="font-bold text-sm">Provision New CIDR Range</h3>
            <p className="text-xs text-blue-100">Register your IP range with AWS BYOIP</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-blue-200 text-xl font-bold leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              CIDR Block <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={cidr}
              onChange={e => setCidr(e.target.value)}
              placeholder="e.g. 203.0.113.0/24"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-0.5">Minimum /24 for IPv4, /48 for IPv6</p>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Production US East"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">AWS Region</label>
            <select
              value={region}
              onChange={e => setRegion(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">
              ROA Message
              <span className="text-xs text-gray-400 font-normal ml-1">(from your RIR/RPKI)</span>
            </label>
            <input
              type="text"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="1|{aws-account-id}|{cidr}|{expiry}|SHA256withRSA"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Signature (Base64)</label>
            <textarea
              value={signature}
              onChange={e => setSignature(e.target.value)}
              placeholder="Paste your base64-encoded signature here..."
              rows={3}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-700">
            <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
            AWS will verify ownership and change status to <strong>provisioned</strong> within a few minutes.
            You must then <strong>Advertise</strong> the CIDR to make it live on the internet.
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !cidr.trim()}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {loading
                ? <><FontAwesomeIcon icon={faSpinner} spin className="mr-1" />Provisioning...</>
                : <><FontAwesomeIcon icon={faPlus} className="mr-1" />Provision CIDR</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Tab ─────────────────────────────────────────────────────────────────

const BYOIPTab: React.FC = () => {
  // ── Dashboard state ────────────────────────────────────────────────────────
  const [cidrs, setCidrs] = useState<CidrRange[]>([]);
  const [eips, setEips] = useState<ElasticIP[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeView, setActiveView] = useState<'cidrs' | 'eips'>('cidrs');
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  // ── Main view toggle ───────────────────────────────────────────────────────
  const [mainView, setMainView] = useState<'dashboard' | 'browse' | 'cart'>('dashboard');

  // ── Browse / Search state ──────────────────────────────────────────────────
  const [browseSize, setBrowseSize] = useState('/24');
  const [browseRirs, setBrowseRirs] = useState<string[]>([]);
  const [browseCountry, setBrowseCountry] = useState('');
  const [browseCity, setBrowseCity] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [sizeExpanded, setSizeExpanded] = useState(true);
  const [rirExpanded, setRirExpanded] = useState(true);
  const [locationExpanded, setLocationExpanded] = useState(true);
  const [browseSubnets, setBrowseSubnets] = useState<LeasingSubnet[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [geoModalSubnet, setGeoModalSubnet] = useState<LeasingSubnet | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartToast, setCartToast] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'price_asc' | 'price_desc'>('price_asc');
  // Cart page state
  const [selectedCartIds, setSelectedCartIds] = useState<Set<string>>(new Set());
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // ── Dashboard effects ──────────────────────────────────────────────────────
  const loadDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE_URL}/api/byoip/cidrs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCidrs(res.ok && data.status === 'success' ? (data.cidrs || []) : []);
    } catch {
      setCidrs([]);
    }
    setEips(MOCK_EIPS);
    setLoading(false);
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const stats = calcStats(cidrs);

  // ── Browse handlers ────────────────────────────────────────────────────────
  const handleBrowseSearch = async () => {
    setBrowseLoading(true);
    setBrowseError(null);
    setHasSearched(true);
    try {
      const params = new URLSearchParams();
      if (browseSize) params.append('size', browseSize);
      if (browseRirs.length > 0) params.append('rir', browseRirs.join(','));
      if (browseCountry.trim()) params.append('country', browseCountry.trim());
      if (browseCity.trim()) params.append('city', browseCity.trim());

      const res = await fetch(`${API_BASE_URL}/api/byoip/subnets?${params.toString()}`);
      const data = await res.json();
      if (!res.ok || data.status !== 'success') throw new Error(data.message || 'Failed to search subnets');
      setBrowseSubnets(data.subnets || []);
    } catch (err: any) {
      setBrowseError(err.message || 'Failed to search subnets');
    } finally {
      setBrowseLoading(false);
    }
  };

  const toggleRir = (rir: string) => {
    setBrowseRirs(prev => prev.includes(rir) ? prev.filter(r => r !== rir) : [...prev, rir]);
  };

  const handleAddToCart = (subnet: LeasingSubnet) => {
    setCartItems(prev => {
      if (prev.find(c => c.subnet.id === subnet.id)) return prev; // already in cart
      return [...prev, { subnet, addedAt: new Date().toISOString() }];
    });
    setCartToast(subnet.subnet);
    setTimeout(() => setCartToast(null), 2500);
  };

  const handleRemoveFromCart = (subnetId: string) => {
    setCartItems(prev => prev.filter(c => c.subnet.id !== subnetId));
    setSelectedCartIds(prev => { const s = new Set(prev); s.delete(subnetId); return s; });
  };

  const toggleCartSelect = (subnetId: string) => {
    setSelectedCartIds(prev => {
      const s = new Set(prev);
      s.has(subnetId) ? s.delete(subnetId) : s.add(subnetId);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCartIds.size === cartItems.length) {
      setSelectedCartIds(new Set());
    } else {
      setSelectedCartIds(new Set(cartItems.map(c => c.subnet.id)));
    }
  };

  const cartSubtotal = cartItems.reduce((sum, c) => sum + c.subnet.subnetPriceMonthly, 0);
  const cartTotal = discountApplied ? cartSubtotal * 0.9 : cartSubtotal;

  const getBillingPeriod = () => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const fmt = (d: Date) =>
      `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    return `${fmt(today)} - ${fmt(nextMonth)}`;
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE_URL}/api/byoip/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subnetIds: cartItems.map(c => c.subnet.id) }),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 'success' || !data.checkout?.url) {
        throw new Error(data.message || 'Failed to start checkout');
      }
      window.location.href = data.checkout.url;
    } catch (err: any) {
      setCheckoutError(err.message || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleBackToFilters = () => {
    setHasSearched(false);
    setBrowseSubnets([]);
    setBrowseError(null);
  };

  const removeFilter = (type: 'size' | 'rir' | 'country' | 'city', value?: string) => {
    if (type === 'size') setBrowseSize('/24');
    if (type === 'rir' && value) setBrowseRirs(prev => prev.filter(r => r !== value));
    if (type === 'country') setBrowseCountry('');
    if (type === 'city') setBrowseCity('');
  };

  const sortedSubnets = [...browseSubnets].sort((a, b) =>
    sortBy === 'price_asc'
      ? a.subnetPriceMonthly - b.subnetPriceMonthly
      : b.subnetPriceMonthly - a.subnetPriceMonthly
  );

  const activeFilterCount =
    (browseSize ? 1 : 0) +
    browseRirs.length +
    (browseCountry.trim() ? 1 : 0) +
    (browseCity.trim() ? 1 : 0);

  // ── Dashboard handlers ─────────────────────────────────────────────────────
  const handleAdvertise = async (cidr: string) => {
    setActionLoading(cidr);
    await new Promise(r => setTimeout(r, 1500));
    setCidrs(prev => prev.map(c => c.cidr === cidr ? { ...c, status: 'advertised' } : c));
    setActionLoading(null);
  };

  const handleDeAdvertise = async (cidr: string) => {
    if (!window.confirm(`De-advertise ${cidr}? Traffic to these IPs will stop routing.`)) return;
    setActionLoading(cidr);
    await new Promise(r => setTimeout(r, 1500));
    setCidrs(prev => prev.map(c => c.cidr === cidr ? { ...c, status: 'provisioned' } : c));
    setActionLoading(null);
  };

  const handleDeprovision = async (cidr: string) => {
    if (!window.confirm(`Deprovision ${cidr}? This will remove the CIDR from your AWS account.`)) return;
    setActionLoading(cidr);
    await new Promise(r => setTimeout(r, 1500));
    setCidrs(prev => prev.filter(c => c.cidr !== cidr));
    setActionLoading(null);
  };

  const handleAddCidr = (cidr: string, region: string, description: string) => {
    const prefix = parseInt(cidr.split('/')[1]);
    const totalIPs = Math.pow(2, 32 - prefix);
    const newCidr: CidrRange = {
      cidr,
      status: 'pending-provision',
      region,
      poolId: `ipv4pool-ec2-00${cidrs.length + 4}`,
      totalIPs,
      usedIPs: 0,
      description: description || undefined,
      createdAt: new Date().toISOString(),
    };
    setCidrs(prev => [...prev, newCidr]);
    setShowAddModal(false);
  };

  const handleAllocateEIP = async (poolId: string, cidr: string, region: string) => {
    setActionLoading(`allocate-${poolId}`);
    await new Promise(r => setTimeout(r, 1200));
    const lastOctet = Math.floor(Math.random() * 200) + 20;
    const baseIP = cidr.split('/')[0].split('.').slice(0, 3).join('.');
    const newEip: ElasticIP = {
      allocationId: `eipalloc-${Math.random().toString(36).slice(2, 12)}`,
      publicIp: `${baseIP}.${lastOctet}`,
      poolId,
      cidr,
      associatedWith: null,
      associationType: null,
      region,
      allocatedAt: new Date().toISOString(),
    };
    setEips(prev => [...prev, newEip]);
    setCidrs(prev => prev.map(c => c.poolId === poolId ? { ...c, usedIPs: c.usedIPs + 1 } : c));
    setActionLoading(null);
    setActiveView('eips');
  };

  const handleReleaseEIP = async (allocationId: string) => {
    if (!window.confirm('Release this Elastic IP back to the BYOIP pool?')) return;
    setActionLoading(allocationId);
    await new Promise(r => setTimeout(r, 1000));
    setEips(prev => prev.filter(e => e.allocationId !== allocationId));
    setActionLoading(null);
  };

  const handleRenewLease = async (cidrId: string) => {
    setActionLoading(`renew-${cidrId}`);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE_URL}/api/byoip/cidrs/${cidrId}/renew`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || data.status !== 'success' || !data.checkout?.url) {
        throw new Error(data.message || 'Failed to start renewal checkout');
      }
      window.location.href = data.checkout.url;
    } catch (err: any) {
      alert(err.message || 'Failed to start renewal checkout');
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopyFeedback(text);
    setTimeout(() => setCopyFeedback(null), 1500);
  };

  const handleRefresh = async () => {
    await loadDashboard();
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">
        <div className="animate-pulse">
          <div className="h-16 bg-gray-200 rounded mb-3" />
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-16 bg-gray-200 rounded" />)}
          </div>
          <div className="h-48 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  const utilPct = stats.totalIPs > 0 ? Math.round((stats.usedIPs / stats.totalIPs) * 100) : 0;

  return (
    <div className="p-3 space-y-3 bg-gray-50 min-h-screen font-mono">

      {/* ── Cart Toast ─────────────────────────────────────────────────────── */}
      {cartToast && (
        <div className="fixed top-4 right-4 z-50 bg-green-700 text-white text-xs px-4 py-2 rounded shadow-lg flex items-center gap-2">
          <FontAwesomeIcon icon={faCheck} />
          <span><code>{cartToast}</code> added to cart</span>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faGlobe} className="text-blue-600" />
              <h1 className="text-base font-bold text-gray-900">BYOIP</h1>
              <span className="text-xs px-2 py-0.5 rounded font-medium bg-blue-100 text-blue-700">
                Bring Your Own IP
              </span>
              {mainView === 'browse' && (
                <span className="text-xs px-2 py-0.5 rounded font-medium bg-green-100 text-green-700">
                  IP Leasing
                </span>
              )}
              {mainView === 'cart' && (
                <span className="text-xs px-2 py-0.5 rounded font-medium bg-orange-100 text-orange-700">
                  Shopping Cart
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {mainView === 'browse'
                ? 'Browse and lease available IPv4 subnets from trusted IP holders'
                : mainView === 'cart'
                ? 'Review your selected subnets before proceeding to checkout'
                : 'Manage your own IP address ranges on AWS • Provision, advertise, and allocate Elastic IPs'}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {cartItems.length > 0 && mainView !== 'cart' && (
              <button
                onClick={() => setMainView('cart')}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-800 text-white rounded font-medium hover:bg-blue-900 transition-colors"
              >
                <FontAwesomeIcon icon={faShoppingCart} />
                Cart ({cartItems.length})
              </button>
            )}
            {mainView === 'dashboard' && (
              <>
                <button
                  onClick={handleRefresh}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 font-medium"
                >
                  <FontAwesomeIcon icon={faSync} className="mr-1" />Refresh
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 font-medium"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />Add CIDR
                </button>
                <button
                  onClick={() => { setMainView('browse'); setHasSearched(false); }}
                  className="px-3 py-1.5 text-xs bg-blue-700 text-white rounded hover:bg-blue-800 font-medium"
                >
                  <FontAwesomeIcon icon={faSearch} className="mr-1" />Browse IPs
                </button>
              </>
            )}
            {mainView === 'browse' && (
              <button
                onClick={() => { setMainView('dashboard'); setHasSearched(false); }}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 font-medium"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />My IP Blocks
              </button>
            )}
            {mainView === 'cart' && (
              <button
                onClick={() => setMainView('browse')}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 font-medium"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />Continue Browsing
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Stats Cards (dashboard only) ────────────────────────────────────── */}
      {mainView === 'dashboard' && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="text-xs text-gray-600 font-medium">CIDR Ranges</div>
            <div className="text-xl font-bold text-gray-900">{stats.totalCidrs}</div>
            <div className="text-xs text-gray-500 mt-0.5">Total registered</div>
          </div>
          <div className="bg-white border border-green-300 rounded p-2">
            <div className="text-xs text-gray-600 font-medium">Advertised</div>
            <div className="text-xl font-bold text-green-700">{stats.advertisedCidrs}</div>
            <div className="text-xs text-gray-500 mt-0.5">Live on internet</div>
          </div>
          <div className="bg-white border border-yellow-300 rounded p-2">
            <div className="text-xs text-gray-600 font-medium">Provisioned</div>
            <div className="text-xl font-bold text-yellow-700">{stats.provisionedCidrs}</div>
            <div className="text-xs text-gray-500 mt-0.5">Not advertised</div>
          </div>
          <div className="bg-white border border-gray-300 rounded p-2">
            <div className="text-xs text-gray-600 font-medium">Total IPs</div>
            <div className="text-xl font-bold text-gray-900">{stats.totalIPs.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-0.5">Across all CIDRs</div>
          </div>
          <div className="bg-white border border-orange-300 rounded p-2">
            <div className="text-xs text-gray-600 font-medium">IPs In Use</div>
            <div className="text-xl font-bold text-orange-700">{stats.usedIPs}</div>
            <div className="text-xs text-gray-500 mt-0.5">{utilPct}% utilized</div>
          </div>
          <div className="bg-white border border-blue-300 rounded p-2">
            <div className="text-xs text-gray-600 font-medium">Available IPs</div>
            <div className="text-xl font-bold text-blue-700">{stats.availableIPs.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-0.5">Free to allocate</div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          BROWSE VIEW — IPXO-style IPv4 Subnet Leasing
      ══════════════════════════════════════════════════════════════════════ */}
      {mainView === 'browse' && (
        <div className="space-y-3">

          {/* Filter form (before search) */}
          {!hasSearched && (
            <>
              {/* Title */}
              <div className="bg-white border border-gray-300 rounded p-4 text-center">
                <h2 className="text-lg font-bold text-gray-900 mb-1">Find IPv4 Subnets for Lease</h2>
                <p className="text-xs text-gray-500">
                  Browse and lease available IPv4 subnets from trusted IP holders
                </p>
              </div>

              {/* Filters panel */}
              <div className="bg-white border border-gray-300 rounded divide-y divide-gray-200">

                {/* Subnet size */}
                <div className="p-4">
                  <button
                    onClick={() => setSizeExpanded(!sizeExpanded)}
                    className="flex items-center justify-between w-full mb-0"
                  >
                    <span className="text-sm font-bold text-gray-900">Subnet size</span>
                    <FontAwesomeIcon
                      icon={sizeExpanded ? faChevronUp : faChevronDown}
                      className="text-gray-400 text-xs"
                    />
                  </button>
                  {sizeExpanded && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {SUBNET_SIZES.map(({ size, ips }) => (
                        <button
                          key={size}
                          onClick={() => setBrowseSize(size)}
                          className={`px-3 py-2 rounded border text-xs font-mono transition-colors ${
                            browseSize === size
                              ? 'border-blue-700 bg-blue-50 text-blue-800 font-bold'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <span className="font-bold">{size}</span>
                          <span className={`ml-1.5 font-normal ${browseSize === size ? 'text-blue-600' : 'text-gray-400'}`}>
                            {ips} IPs
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* RIR */}
                <div className="p-4">
                  <button
                    onClick={() => setRirExpanded(!rirExpanded)}
                    className="flex items-center justify-between w-full"
                  >
                    <span className="text-sm font-bold text-gray-900">RIR</span>
                    <FontAwesomeIcon
                      icon={rirExpanded ? faChevronUp : faChevronDown}
                      className="text-gray-400 text-xs"
                    />
                  </button>
                  {rirExpanded && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {RIR_OPTIONS.map(rir => (
                        <button
                          key={rir}
                          onClick={() => toggleRir(rir)}
                          className={`px-4 py-2 rounded border text-xs font-bold transition-colors ${
                            browseRirs.includes(rir)
                              ? 'border-blue-700 bg-blue-50 text-blue-800'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
                          }`}
                        >
                          {rir}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Location */}
                <div className="p-4">
                  <button
                    onClick={() => setLocationExpanded(!locationExpanded)}
                    className="flex items-center justify-between w-full"
                  >
                    <span className="text-sm font-bold text-gray-900">Location</span>
                    <FontAwesomeIcon
                      icon={locationExpanded ? faChevronUp : faChevronDown}
                      className="text-gray-400 text-xs"
                    />
                  </button>
                  {locationExpanded && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <input
                        type="text"
                        value={browseCountry}
                        onChange={e => setBrowseCountry(e.target.value)}
                        placeholder="Country (e.g. Germany)"
                        className="border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                      />
                      <input
                        type="text"
                        value={browseCity}
                        onChange={e => setBrowseCity(e.target.value)}
                        placeholder="City"
                        className="border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                      />
                    </div>
                  )}
                </div>

                {/* More Filters */}
                <div className="p-4">
                  <button
                    onClick={() => setShowMoreFilters(!showMoreFilters)}
                    className="flex items-center gap-1 text-blue-600 text-xs font-medium hover:underline"
                  >
                    More Filters
                    <FontAwesomeIcon icon={showMoreFilters ? faChevronUp : faChevronDown} className="text-xs" />
                  </button>
                  {showMoreFilters && (
                    <div className="mt-3 text-xs text-gray-500 bg-gray-50 rounded p-3 border border-gray-200">
                      Additional filters (RPKI status, price range) — coming soon
                    </div>
                  )}
                </div>
              </div>

              {/* Search button */}
              <button
                onClick={handleBrowseSearch}
                disabled={browseLoading}
                className="w-full py-3 bg-blue-800 text-white rounded font-bold text-sm hover:bg-blue-900 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              >
                {browseLoading
                  ? <><FontAwesomeIcon icon={faSpinner} spin />Searching...</>
                  : <><FontAwesomeIcon icon={faSearch} />Search Subnets</>}
              </button>
            </>
          )}

          {/* Results (after search) */}
          {hasSearched && (
            <>
              {/* Results header */}
              <div className="bg-white border border-gray-300 rounded p-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold text-gray-900">Find IPv4 Subnets for Lease</h2>
                  <button
                    onClick={handleBackToFilters}
                    className="px-3 py-1.5 text-xs border border-blue-600 text-blue-700 rounded font-medium hover:bg-blue-50 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faSearch} />
                    Search by filter ({activeFilterCount})
                  </button>
                </div>

                {/* Active filter chips */}
                <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                  <span className="text-xs text-gray-500 font-medium">Filters:</span>
                  {browseSize && (
                    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded border border-gray-200">
                      Size: {browseSize}
                      <button
                        onClick={() => { removeFilter('size'); handleBrowseSearch(); }}
                        className="ml-0.5 hover:text-red-600 leading-none"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {browseRirs.map(rir => (
                    <span key={rir} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded border border-gray-200">
                      {rir}
                      <button
                        onClick={() => { removeFilter('rir', rir); }}
                        className="ml-0.5 hover:text-red-600 leading-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {browseCountry && (
                    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded border border-gray-200">
                      Country: {browseCountry}
                      <button onClick={() => removeFilter('country')} className="ml-0.5 hover:text-red-600">×</button>
                    </span>
                  )}
                  {browseCity && (
                    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded border border-gray-200">
                      City: {browseCity}
                      <button onClick={() => removeFilter('city')} className="ml-0.5 hover:text-red-600">×</button>
                    </span>
                  )}
                </div>

                {/* Sort */}
                <div className="flex justify-end mt-2">
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value as any)}
                    className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none text-gray-700"
                  >
                    <option value="price_asc">Sort by: Price: Low → High</option>
                    <option value="price_desc">Sort by: Price: High → Low</option>
                  </select>
                </div>
              </div>

              {/* Error */}
              {browseError && (
                <div className="bg-red-50 border border-red-300 rounded p-3 text-xs text-red-700">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                  {browseError}
                </div>
              )}

              {/* Loading */}
              {browseLoading && (
                <div className="bg-white border border-gray-300 rounded p-8 text-center text-xs text-gray-500">
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />Searching subnets...
                </div>
              )}

              {/* No results */}
              {!browseLoading && !browseError && sortedSubnets.length === 0 && (
                <div className="bg-white border border-gray-300 rounded p-8 text-center">
                  <FontAwesomeIcon icon={faGlobe} className="text-3xl text-gray-300 mb-2 block mx-auto" />
                  <p className="text-sm font-medium text-gray-600">No subnets found</p>
                  <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or selecting a different subnet size</p>
                </div>
              )}

              {/* Result cards */}
              {!browseLoading && sortedSubnets.map(s => (
                <div key={s.id} className="bg-white border border-gray-300 rounded p-4">
                  <div className="flex items-start gap-4 flex-wrap">

                    {/* Subnet */}
                    <div className="min-w-[160px]">
                      <div className="text-xs text-gray-500 font-medium mb-1">Subnet</div>
                      <code className="text-sm font-bold text-gray-900">{s.subnet}</code>
                    </div>

                    {/* RIR */}
                    <div>
                      <div className="text-xs text-gray-500 font-medium mb-1">RIR</div>
                      <span className="text-sm font-bold text-gray-800">{s.rir}</span>
                    </div>

                    {/* GEO */}
                    <div>
                      <div className="text-xs text-gray-500 font-medium mb-1">GEO</div>
                      <div className="flex items-center gap-1.5">
                        <FontAwesomeIcon
                          icon={faMapMarkerAlt}
                          className={`text-xs ${
                            s.countryMatch === 'Full Match' ? 'text-green-500' :
                            s.countryMatch === 'Partial Match' ? 'text-yellow-500' : 'text-red-500'
                          }`}
                        />
                        <span className="text-xs font-medium text-gray-700">
                          {s.countryMatch === 'Full Match' ? s.geoCountryName : 'Detected Mismatch'}
                        </span>
                        <button
                          onClick={() => setGeoModalSubnet(s)}
                          className="text-blue-600 text-xs hover:underline font-medium"
                        >
                          View Data
                        </button>
                      </div>
                    </div>

                    {/* Subnet Price */}
                    <div>
                      <div className="text-xs text-gray-500 font-medium mb-1">Subnet Price</div>
                      <span className="text-sm font-bold text-gray-900">${s.subnetPriceMonthly.toFixed(2)} /mo</span>
                    </div>

                    {/* Price per IP */}
                    <div>
                      <div className="text-xs text-gray-500 font-medium mb-1">Price per IP</div>
                      <span className="text-sm font-bold text-gray-900">${s.pricePerIPMonthly.toFixed(3)} /mo</span>
                    </div>

                    {/* Add to Cart */}
                    <div className="ml-auto">
                      <button
                        onClick={() => handleAddToCart(s)}
                        className="px-4 py-2 bg-blue-800 text-white text-xs font-bold rounded hover:bg-blue-900 flex items-center gap-1.5 whitespace-nowrap transition-colors"
                      >
                        <FontAwesomeIcon icon={faShoppingCart} />
                        Add to Cart
                      </button>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium border ${
                      s.countryMatch === 'Full Match'
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : s.countryMatch === 'Partial Match'
                        ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      Country: {s.countryMatch}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded font-medium bg-gray-50 text-gray-600 border border-gray-200">
                      RPKI: {s.rpki}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded font-medium bg-gray-50 text-gray-600 border border-gray-200">
                      rDNS: {s.rdns}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded font-medium bg-gray-50 text-gray-600 border border-gray-200">
                      WHOIS Inetnum: {s.whoisInetnum}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded font-medium bg-gray-50 text-gray-600 border border-gray-200">
                      WHOIS Routes: {s.whoisRoutes}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          CART VIEW — Shopping Cart + Cart Totals
      ══════════════════════════════════════════════════════════════════════ */}
      {mainView === 'cart' && (
        <div className="space-y-3">

          {/* Step indicator */}
          <div className="bg-white border border-gray-300 rounded p-3">
            <div className="flex items-center justify-center gap-0">
              {/* Step 1 */}
              <div className="flex items-center gap-2 px-5 py-2 bg-blue-800 text-white rounded-l text-xs font-bold">
                <span className="w-5 h-5 rounded-full bg-white text-blue-800 flex items-center justify-center text-xs font-bold">1</span>
                <FontAwesomeIcon icon={faShoppingCart} />
                Shopping Cart
              </div>
              {/* Step 2 */}
              <div className="flex items-center gap-2 px-5 py-2 bg-gray-100 text-gray-400 rounded-r border border-gray-300 border-l-0 text-xs font-bold">
                <span className="w-5 h-5 rounded-full bg-gray-300 text-gray-500 flex items-center justify-center text-xs font-bold">2</span>
                Checkout
              </div>
            </div>
          </div>

          <div className="flex gap-3 items-start">

            {/* Left: Product Table */}
            <div className="flex-1 min-w-0">
              <div className="bg-white border border-gray-300 rounded overflow-hidden">

                {cartItems.length === 0 ? (
                  <div className="text-center p-12">
                    <FontAwesomeIcon icon={faShoppingCart} className="text-4xl text-gray-300 mb-3 block mx-auto" />
                    <p className="text-sm font-medium text-gray-600">Your cart is empty</p>
                    <p className="text-xs text-gray-400 mt-1">Browse IPs and add subnets to your cart</p>
                    <button
                      onClick={() => setMainView('browse')}
                      className="mt-4 px-4 py-2 bg-blue-800 text-white text-xs font-bold rounded hover:bg-blue-900"
                    >
                      <FontAwesomeIcon icon={faSearch} className="mr-1" />Browse IPs
                    </button>
                  </div>
                ) : (
                  <>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="p-3 w-8">
                            <input
                              type="checkbox"
                              checked={selectedCartIds.size === cartItems.length && cartItems.length > 0}
                              onChange={toggleSelectAll}
                              className="cursor-pointer"
                            />
                          </th>
                          <th className="text-left p-3 font-bold text-gray-700">Product</th>
                          <th className="text-left p-3 font-bold text-gray-700">Price</th>
                          <th className="p-3 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {cartItems.map(item => (
                          <tr key={item.subnet.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-3">
                              <input
                                type="checkbox"
                                checked={selectedCartIds.has(item.subnet.id)}
                                onChange={() => toggleCartSelect(item.subnet.id)}
                                className="cursor-pointer"
                              />
                            </td>
                            <td className="p-3">
                              <code className="font-bold text-gray-900 text-sm">{item.subnet.subnet}</code>
                              <div className="flex gap-2 mt-1">
                                <span className="text-gray-400">RIR: {item.subnet.rir}</span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-400">{item.subnet.totalIPs.toLocaleString()} IPs</span>
                                <span className="text-gray-400">•</span>
                                <span className={`font-medium ${
                                  item.subnet.countryMatch === 'Full Match' ? 'text-green-600' :
                                  item.subnet.countryMatch === 'Partial Match' ? 'text-yellow-600' : 'text-red-600'
                                }`}>{item.subnet.countryMatch}</span>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="font-bold text-gray-900">${item.subnet.subnetPriceMonthly.toFixed(2)}</div>
                              <div className="text-gray-400">${item.subnet.pricePerIPMonthly.toFixed(3)} per IP/mo</div>
                            </td>
                            <td className="p-3">
                              <button
                                onClick={() => handleRemoveFromCart(item.subnet.id)}
                                className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                title="Remove from cart"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination row */}
                    <div className="flex items-center justify-between p-3 border-t border-gray-100 text-xs text-gray-500">
                      <div className="flex items-center gap-2">
                        Items per page:
                        <select className="border border-gray-300 rounded px-1 py-0.5 text-xs focus:outline-none">
                          <option>50</option>
                          <option>25</option>
                          <option>10</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>1 – {cartItems.length} of {cartItems.length}</span>
                        <button disabled className="p-1 text-gray-300">‹</button>
                        <button disabled className="p-1 text-gray-300">›</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right: Cart Totals */}
            <div className="w-72 shrink-0">
              <div className="bg-white border border-gray-300 rounded p-4 space-y-4">
                <h3 className="font-bold text-gray-900 text-sm">Cart Totals</h3>

                {/* Discount Code */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={e => setDiscountCode(e.target.value)}
                    placeholder="Discount Code"
                    className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => {
                      if (discountCode.trim()) setDiscountApplied(true);
                    }}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 font-medium text-gray-700"
                  >
                    Apply
                  </button>
                </div>
                {discountApplied && (
                  <div className="text-xs text-green-600 font-medium flex items-center gap-1">
                    <FontAwesomeIcon icon={faCheck} />
                    10% discount applied
                  </div>
                )}

                <div className="h-px bg-gray-200" />

                {/* Anchor Day */}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1 text-gray-700">
                    Anchor Day
                    <span className="text-gray-400" title="Your billing cycle anchor date">ⓘ</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700 border border-green-200">Enabled</span>
                    <span className="text-gray-600 font-medium">Anchor Day 1</span>
                  </div>
                </div>

                {/* Billing period */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-700">Billing period</span>
                  <span className="text-gray-600 font-medium">{getBillingPeriod()}</span>
                </div>

                <div className="h-px bg-gray-200" />

                {/* Subtotal */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-700">Subtotal</span>
                  <span className="font-bold text-gray-900">${cartSubtotal.toFixed(2)} USD</span>
                </div>

                {discountApplied && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-green-600">Discount (10%)</span>
                    <span className="font-medium text-green-600">-${(cartSubtotal * 0.1).toFixed(2)} USD</span>
                  </div>
                )}

                {/* Total */}
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-900 text-sm">Total</span>
                  <span className="font-bold text-gray-900 text-base">${cartTotal.toFixed(2)} USD</span>
                </div>

                {/* Checkout button */}
                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading || cartItems.length === 0}
                  className="w-full py-2.5 bg-blue-800 text-white text-sm font-bold rounded hover:bg-blue-900 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {checkoutLoading
                    ? <><FontAwesomeIcon icon={faSpinner} spin />Redirecting to Stripe…</>
                    : <><FontAwesomeIcon icon={faShoppingCart} />Proceed to checkout</>}
                </button>
                {checkoutError && (
                  <p className="text-center text-xs text-red-600">{checkoutError}</p>
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          DASHBOARD VIEW — existing CIDR + EIP management
      ══════════════════════════════════════════════════════════════════════ */}
      {mainView === 'dashboard' && (
        <>
          {/* Alert: Provisioned but Not Advertised */}
          {cidrs.some(c => c.status === 'provisioned') && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 flex items-start gap-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 text-sm mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-bold text-yellow-900">CIDR Not Advertised</p>
                <p className="text-xs text-yellow-700 mt-0.5">
                  <strong>{cidrs.filter(c => c.status === 'provisioned').map(c => c.cidr).join(', ')}</strong> —
                  provisioned but not advertised. IPs will <strong>NOT</strong> route until you click Advertise.
                </p>
              </div>
            </div>
          )}

          {/* Sub-Tab Toggle */}
          <div className="bg-white border border-gray-300 rounded">
            <div className="flex space-x-1 p-1">
              <button
                onClick={() => setActiveView('cidrs')}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  activeView === 'cidrs' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FontAwesomeIcon icon={faNetworkWired} className="mr-1" />
                CIDR Pool ({cidrs.length})
              </button>
              <button
                onClick={() => setActiveView('eips')}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  activeView === 'eips' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FontAwesomeIcon icon={faGlobe} className="mr-1" />
                Elastic IPs ({eips.length})
              </button>
            </div>
          </div>

          {/* CIDR Pool Table */}
          {activeView === 'cidrs' && (
            <div className="bg-white border border-gray-300 rounded overflow-hidden">
              <div className="p-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <h3 className="text-sm font-bold text-gray-900">
                  <FontAwesomeIcon icon={faNetworkWired} className="mr-1 text-blue-600" />
                  CIDR Ranges
                </h3>
                <span className="text-xs text-gray-500">{cidrs.length} range{cidrs.length !== 1 ? 's' : ''} registered</span>
              </div>

              {cidrs.length === 0 ? (
                <div className="text-center p-10 text-gray-500">
                  <FontAwesomeIcon icon={faNetworkWired} className="text-4xl mb-3 block mx-auto text-gray-300" />
                  <div className="text-sm font-medium">No CIDR ranges registered</div>
                  <div className="text-xs mt-1">Click "Add CIDR Range" to provision your first IP block</div>
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 border-b border-gray-300">
                    <tr>
                      <th className="text-left p-2 font-bold">CIDR Block</th>
                      <th className="text-left p-2 font-bold">Status</th>
                      <th className="text-left p-2 font-bold">Region</th>
                      <th className="text-left p-2 font-bold">Pool ID</th>
                      <th className="text-left p-2 font-bold">IPs Used / Total</th>
                      <th className="text-left p-2 font-bold">Utilization</th>
                      <th className="text-left p-2 font-bold">Renewal</th>
                      <th className="text-right p-2 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cidrs.map(c => {
                      const pct = c.totalIPs > 0 ? Math.round((c.usedIPs / c.totalIPs) * 100) : 0;
                      const isActioning = actionLoading === c.cidr;
                      const isRenewing = actionLoading === `renew-${c.id}`;
                      const isExpired = !!c.leaseExpiresAt && new Date(c.leaseExpiresAt) < new Date();
                      return (
                        <tr key={c.cidr} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="p-2">
                            <code className="font-bold text-gray-900">{c.cidr}</code>
                            {c.description && <div className="text-xs text-gray-500 mt-0.5">{c.description}</div>}
                          </td>
                          <td className="p-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${getStatusColor(c.status)}`}>
                              {(c.status === 'pending-provision' || c.status === 'pending-de-provision') && (
                                <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                              )}
                              {getStatusLabel(c.status)}
                            </span>
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 text-xs" />
                              <span className="font-medium text-gray-700">{c.region}</span>
                            </div>
                          </td>
                          <td className="p-2">
                            <code className="text-xs text-gray-500">{c.poolId}</code>
                          </td>
                          <td className="p-2 font-medium">
                            <span className={c.usedIPs > 0 ? 'text-orange-700' : 'text-gray-600'}>{c.usedIPs}</span>
                            <span className="text-gray-400"> / {c.totalIPs}</span>
                          </td>
                          <td className="p-2 w-28">
                            <div className="flex items-center gap-1">
                              <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full transition-all ${
                                    pct >= 80 ? 'bg-red-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-gray-600 w-8 text-right">{pct}%</span>
                            </div>
                          </td>
                          <td className="p-2">
                            {!c.leaseExpiresAt ? (
                              <span className="text-gray-400">—</span>
                            ) : isExpired ? (
                              <button
                                onClick={() => c.id && handleRenewLease(c.id)}
                                disabled={isRenewing || !c.id}
                                className="px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 font-medium"
                              >
                                {isRenewing
                                  ? <FontAwesomeIcon icon={faSpinner} spin />
                                  : <>Renew — expired {new Date(c.leaseExpiresAt).toLocaleDateString()}</>}
                              </button>
                            ) : (
                              <span className="text-gray-600">
                                Renews by {new Date(c.leaseExpiresAt).toLocaleDateString()}
                              </span>
                            )}
                          </td>
                          <td className="p-2">
                            <div className="flex justify-end gap-1 flex-wrap">
                              {c.status === 'provisioned' && (
                                <button
                                  onClick={() => handleAdvertise(c.cidr)}
                                  disabled={isActioning}
                                  className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 font-medium"
                                >
                                  {isActioning ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faBroadcastTower} className="mr-1" />Advertise</>}
                                </button>
                              )}
                              {c.status === 'advertised' && (
                                <button
                                  onClick={() => handleDeAdvertise(c.cidr)}
                                  disabled={isActioning}
                                  className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50 font-medium"
                                >
                                  {isActioning ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faStop} className="mr-1" />De-Advertise</>}
                                </button>
                              )}
                              {c.status === 'advertised' && (
                                <button
                                  onClick={() => handleAllocateEIP(c.poolId, c.cidr, c.region)}
                                  disabled={!!actionLoading}
                                  className="px-2 py-1 text-xs border border-blue-300 text-blue-700 rounded hover:bg-blue-50 font-medium"
                                >
                                  {actionLoading === `allocate-${c.poolId}` ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faPlus} className="mr-1" />EIP</>}
                                </button>
                              )}
                              {(c.status === 'pending-provision' || c.status === 'pending-de-provision') && (
                                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded font-medium">AWS Verifying...</span>
                              )}
                              {c.status !== 'pending-provision' && c.status !== 'pending-de-provision' && (
                                <button
                                  onClick={() => handleDeprovision(c.cidr)}
                                  disabled={isActioning}
                                  className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                  title="Deprovision"
                                >
                                  {isActioning ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faTrash} />}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Elastic IPs Table */}
          {activeView === 'eips' && (
            <div className="bg-white border border-gray-300 rounded overflow-hidden">
              <div className="p-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <h3 className="text-sm font-bold text-gray-900">
                  <FontAwesomeIcon icon={faGlobe} className="mr-1 text-blue-600" />
                  Elastic IPs from BYOIP Pool
                </h3>
                <span className="text-xs text-gray-500">{eips.length} IP{eips.length !== 1 ? 's' : ''} allocated</span>
              </div>

              {eips.length === 0 ? (
                <div className="text-center p-10 text-gray-500">
                  <FontAwesomeIcon icon={faGlobe} className="text-4xl mb-3 block mx-auto text-gray-300" />
                  <div className="text-sm font-medium">No Elastic IPs allocated</div>
                  <div className="text-xs mt-1">Allocate an EIP from an advertised CIDR pool above</div>
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 border-b border-gray-300">
                    <tr>
                      <th className="text-left p-2 font-bold">IP Address</th>
                      <th className="text-left p-2 font-bold">Allocation ID</th>
                      <th className="text-left p-2 font-bold">CIDR Pool</th>
                      <th className="text-left p-2 font-bold">Region</th>
                      <th className="text-left p-2 font-bold">Associated With</th>
                      <th className="text-left p-2 font-bold">Status</th>
                      <th className="text-right p-2 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eips.map(eip => {
                      const isActioning = actionLoading === eip.allocationId;
                      return (
                        <tr key={eip.allocationId} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="p-2">
                            <div className="flex items-center gap-1">
                              <code className="font-bold text-gray-900">{eip.publicIp}</code>
                              <button
                                onClick={() => copyToClipboard(eip.publicIp)}
                                className="text-blue-500 hover:text-blue-700"
                                title="Copy IP"
                              >
                                <FontAwesomeIcon icon={copyFeedback === eip.publicIp ? faCheck : faCopy} className="text-xs" />
                              </button>
                            </div>
                          </td>
                          <td className="p-2">
                            <code className="text-xs text-gray-500">{eip.allocationId.slice(-12)}</code>
                          </td>
                          <td className="p-2">
                            <code className="text-xs text-gray-600">{eip.cidr}</code>
                          </td>
                          <td className="p-2 text-gray-700 font-medium">
                            <div className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 text-xs" />
                              {eip.region}
                            </div>
                          </td>
                          <td className="p-2">
                            {eip.associatedWith ? (
                              <div>
                                <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700 mr-1">
                                  {eip.associationType?.toUpperCase()}
                                </span>
                                <code className="text-xs text-gray-600">
                                  {eip.associatedWith.length > 22 ? eip.associatedWith.slice(0, 22) + '...' : eip.associatedWith}
                                </code>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Not associated</span>
                            )}
                          </td>
                          <td className="p-2">
                            {eip.associatedWith ? (
                              <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700 border border-green-300">In Use</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-600 border border-gray-300">Free</span>
                            )}
                          </td>
                          <td className="p-2">
                            <div className="flex justify-end gap-1">
                              {!eip.associatedWith && (
                                <button
                                  onClick={() => handleReleaseEIP(eip.allocationId)}
                                  disabled={isActioning}
                                  className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                  title="Release EIP back to pool"
                                >
                                  {isActioning ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faTrash} className="mr-1" />Release</>}
                                </button>
                              )}
                              <button
                                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
                                title={eip.associatedWith ? 'Disassociate' : 'Associate with EC2/NLB'}
                              >
                                <FontAwesomeIcon icon={eip.associatedWith ? faUnlink : faLink} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Info Cards Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faShieldAlt} className="text-blue-600" />
                <span className="text-xs font-bold text-gray-900">Ownership Verification</span>
              </div>
              <p className="text-xs text-gray-600">
                AWS verifies IP ownership via <strong>Route Origin Authorization (ROA)</strong> using RPKI.
                Your RIR (ARIN, RIPE, APNIC) must have a valid ROA record pointing to AWS ASN 16509.
              </p>
            </div>
            <div className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faWifi} className="text-green-600" />
                <span className="text-xs font-bold text-gray-900">Advertise vs Provision</span>
              </div>
              <p className="text-xs text-gray-600">
                <strong>Provisioned</strong> = AWS verified your CIDR. IPs can be allocated but won't route yet.
                <strong> Advertised</strong> = AWS announces your prefix via BGP — IPs are live on the internet.
              </p>
            </div>
            <div className="bg-white border border-gray-300 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faChartBar} className="text-purple-600" />
                <span className="text-xs font-bold text-gray-900">CIDR Sizing (IPv4)</span>
              </div>
              <p className="text-xs text-gray-600">
                AWS requires <strong>minimum /24</strong> (256 IPs) for IPv4 BYOIP.
                Smaller blocks like /25, /26 must be contained within an advertised /24 or larger prefix.
              </p>
            </div>
          </div>

          {/* Add CIDR Modal */}
          {showAddModal && (
            <AddCidrModal
              onClose={() => setShowAddModal(false)}
              onAdd={handleAddCidr}
            />
          )}
        </>
      )}

      {/* GEO Data Modal */}
      {geoModalSubnet && (
        <GeoDataModal
          subnet={geoModalSubnet}
          onClose={() => setGeoModalSubnet(null)}
        />
      )}
    </div>
  );
};

export default BYOIPTab;
